<template>
  <div
    class="leads leads1 leads--1 lp-section width-expand flex-column flex-between flex-stretch"
    :id="`Leads1-${pageUid}`">
    <div class="leads-wrapper flex-column l:flex-row l:flex-stretch">
      <div
        class="content-container flex-column flex-stretch"
        :class="`bg-${backgroundColor}`">
        <div
          class="text-wrapper l:flex-column l:flex-center l:margin-max-top l:margin-xl-bottom">
          <div class="text-content max-width-800 margin-2xl-bottom">
            <h1
              v-if="isNonEmptyString(finalTitle)"
              :class="`c-${titleColor}`"
              class="main-title margin-l-bottom l:margin-2xl-bottom ">
              {{ finalTitle }}
            </h1>
            <div
              v-if="isNonEmptyEditorContent(text1)"
              :class="`c-${textColor}`">
              <content-blocks :content="text1.blocks"></content-blocks>
            </div>
            <div
              v-if="isNonEmptyEditorContent(text2)"
              class="margin-l-top l:margin-xl-top"
              :class="`c-${textColor}`">
              <content-blocks :content="text2.blocks"></content-blocks>
            </div>
          </div>
        </div>

        <div class="features">
          <hr
            class="l:hidden margin-2xl-bottom margin-remove-top"
            :class="`bg-${featureTextColor}`"
            style="opacity: 0.8" />
          <h2
            class="h3 features-title line-height-080 margin-xl-bottom bold text-3xl"
            :class="`c-${featuresMainTitleColor}`"
            v-if="isNonEmptyString(featuresTitle)">
            {{ featuresTitle }}
          </h2>
          <div
            class="flex-center flex-stretch gap-l l:gap-2xl features-wrapper l:visible"
            v-if="features && features?.length > 0">
            <div
              class="feature flex-column flex-middle gap-m"
              v-for="feature in features">
              <img
                :src="feature.image"
                :alt="t('lp.sections.leads1.featureImageAlt')"
                style="max-height: 50px; max-width: 100px" />
              <span :class="`c-${featureTextColor}`" class="text-l text-center">
                {{ feature.text }}
              </span>
            </div>
          </div>

          <div
            class="slider-container relative padding-2xl-inline max-width-450 margin-auto-inline">
            <slider
              class="feature-slider width-expand margin-inline-auto"
              v-if="features && features?.length > 0 && isSmallerThanL"
              :config="sliderConfig"
              :navigationBgTheme="featureTextColor"
              :navigationTheme="featureTextColor"
              :navigation-outside="true"
              navigation-theme-type="minimal">
              <slide v-for="feature in features" class="padding-inline">
                <div
                  class="feature flex-column flex-middle gap-m margin-auto-inline width-expand padding border-radius-s"
                  :style="`border: 1px solid var(--c-${featureTextColor}`">
                  <img
                    :src="feature.image"
                    :alt="t('lp.sections.leads1.featureImageAlt')"
                    style="max-height: 50px; max-width: 100px" />
                  <span
                    :class="`c-${featureTextColor}`"
                    class="text-l text-center">
                    {{ feature.text }}
                  </span>
                </div>
              </slide>
            </slider>
          </div>
        </div>
      </div>
      <div
        class="main-image-separator"
        :class="`bg-${imageSeparatorColor}`"></div>
      <div
        class="media-container relative flex flex-end"
        :style="
          $s.isCrawler ? `` : `background-image:url('${image.finalSrc}');`
        ">
        <div
          class="media-content padding-l l:padding-2xl flex-column flex-bottom">
          <div class="flex gap-m l:margin-xl-bottom">
            <div class="logo" v-if="logo.show">
              <a
                :href="logo.finalHref"
                @click.prevent="
                track('leads1|logo|click', { value: logo.alt }).then(
                  logo.navigationHandler
                )
              ">
                <img class="logo-image" v-bind="logo" :src="logo.finalSrc" />
              </a>
            </div>
            <div class="logo2" v-if="logo2?.show">
              <a
                :href="logo2.finalHref"
                @click.prevent="
                track('leads1|logo2|click', { value: logo2.alt }).then(
                  logo2.navigationHandler
                )
              ">
                <img class="logo-image logo-image--2" v-bind="logo2" :src="logo2.finalSrc" />
              </a>
            </div>
          </div>

          <div
            class="form-container l:visible relative padding-xl-horizontal padding-2xl-vertical"
            style="max-width: 320px; min-width: 320px">
            <div
              class="bg absolute-cover z-level-1"
              :class="`bg-${formBackgroundColor}`"
              style="opacity: 0.8"></div>
            <div class="form-inner-wrapper relative z-level-2">
              <h3
                v-show="!isSubmitted"
                :class="`c-${formTitleColor}`"
                class="margin-xl-bottom text-center bold"
                style="line-height: 0.7; font-size: calc(var(--font-max) * 0.9)"
                v-if="isNonEmptyString(formTitle)">
                {{ formTitle }}
              </h3>
              <base-form
                v-if="!isSmallerThanL"
                method="post"
                action="lead/landing-page/lead"
                class="margin-auto-inline"
                v-show="!isSubmitted"
                @form:submit-error="submitError"
                @form:submit-success="submitHandler"
                :auto-set-default-values="true"
                v-model="formData"
                :config="finalFormConfig"></base-form>

              <div
                class="thank-you-container margin-auto-inline text-center"
                :class="` c-${formThankYouTextColor}`"
                v-show="isSubmitted">
                <h3
                  class="thank-you-title margin-remove-bottom bold"
                  :class="` c-${formThankYouTitleColor}`"
                  v-if="isNonEmptyString(formThankYouTitle)">
                  {{ formThankYouTitle }}
                </h3>
                <content-blocks
                  :content="formThankYouText.blocks"
                  v-if="
                    formThankYouText &&
                    formThankYouText.blocks &&
                    formThankYouText.blocks.length > 0
                  "
                  :class="`c-${formThankYouTextColor}`">
                </content-blocks>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      :class="`c-${legalTextColor} bg-${legalBackgroundColor}`"
      class="legal-wrapper padding-l-vertical padding-m-inline">
      <div class="width-expand">
        <p :class="`c-${legalTextColor}`">
          {{ legalText }}
          <span v-if="legalLink.show">
            <span>&nbsp;</span>
            <a
              :class="`c-${legalLinkColor}`"
              v-bind="legalLink"
              @click.pevent="legalLink.navigationHandler">
              {{ legalLink.text }}
            </a>
          </span>
        </p>
      </div>
    </div>

    <div
      class="mobile-spacer l:hidden"
      :style="`height: ${
        floatingCtaButton?.size === 'l' ? '60' : '40'
      }px;`"></div>

    <div class="mobile-floating-cta fixed-bottom-strip l:hidden">
      <form-button
        size="l"
        :auto-translate="false"
        class="width-expand border-radius-remove text-2xl bold"
        v-bind="floatingCtaButton"
        @click.prevent="isPopupFormOpened = true">
      </form-button>
    </div>

    <modal
      :size="'m'"
      :show="isPopupFormOpened"
      @modal:closed="isPopupFormOpened = false"
      class="leads1-mobile-form-modal"
      :class="{ submitted: isSubmitted }"
      :show-footer="false">
      <div
        class="form-container relative margin-auto-inline"
        style="max-width: 400px">
        <div class="form-inner-wrapper relative z-level-2">
          <h3
            v-show="!isSubmitted"
            :class="`c-${formTitleMobileColor}`"
            class="margin-xl-bottom text-center bold text-2xl"
            style="line-height: 0.9"
            v-if="isNonEmptyString(formTitle)">
            {{ formTitle }}
          </h3>
          <base-form
            v-if="isSmallerThanL"
            method="post"
            action="lead/landing-page/lead"
            class="margin-auto-inline"
            v-show="!isSubmitted"
            @form:submit-success="submitHandler"
            :auto-set-default-values="true"
            v-model="formData"
            :config="finalFormConfig"></base-form>

          <div
            class="thank-you-container margin-auto-inline text-center"
            :class="` c-${formThankYouTextColor}`"
            v-show="isSubmitted">
            <h3
              class="h1 thank-you-title margin-remove-bottom bold"
              :class="` c-${formThankYouTitleColor}`"
              v-if="isNonEmptyString(formThankYouTitle)">
              {{ formThankYouTitle }}
            </h3>
            <content-blocks
              class="text-l"
              :content="formThankYouText.blocks"
              v-if="
                formThankYouText &&
                formThankYouText.blocks &&
                formThankYouText.blocks.length > 0
              "
              :class="`c-${formThankYouTextColor}`">
            </content-blocks>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import useUiElements from "@/client/extensions/composition/useUiElements";
import lpSectionMixin from "@/client/applications/meitavlp/extensions/mixins/lpSection";
import { inject } from "vue";
import _ from "lodash/object";
import useDom from "@/client/extensions/composition/useDom";

export default {
  mixins: [lpSectionMixin],

  setup(props) {
    let pageUid = inject("pageUid");
    let { isSmallerThanL } = useDom();
    let schema = {
      logo: {},
      logo2: {},
      image: {},
      formTitle: "",
      formConfig: {},
      formThankYouTitle: "",
      formThankYouText: "",
      checkboxLabelTooltipBase: "",
      checkbox2LabelTooltipBase: "",
      floatingCtaButton: {},
      title: "",
      dynamicTitleOptions: [],
      text1: "",
      text2: "",
      featuresTitle: "",
      features: [
        {
          image: "",
          imageLegend: "",
          text: "",
        },
      ],
      legalText: "",
      legalLink: {},
      backgroundColor: "",
      imageSeparatorColor: "",
      titleColor: "",
      textColor: "",
      featuresMainTitleColor: "",
      featureTextColor: "",
      formBackgroundColor: "",
      formButtonColor: "",
      formTitleColor: "",
      formTitleMobileColor: "",
      formThankYouTitleColor: "",
      formThankYouTextColor: "",
      legalBackgroundColor: "",
      legalTextColor: "",
      legalLinkColor: "",
    };
    let overloadedProps = useUiElements({ input: props.sectionData }).props;
    return {
      ...schema,
      ...overloadedProps,
      allData: overloadedProps,
      pageUid,
      trackEvent: inject("trackEvent"),
      track: inject("track"),
      triggerSectionsEvent: inject("triggerSectionsEvent"),
      isSmallerThanL,
    };
  },
  data: function () {
    return {
      isSubmitted: false,
      isPopupFormOpened: false,
      formData: {
        //   fullName: "אביחי בודק",
        //   email: "propush6@gmail.com",
        //    phoneNumber: "0544894582",
      },
      sliderConfig: {
        loop: true,
        navigationBgTheme: this.featureTextColor,
        navigationTheme: this.backgroundColor,
        slidesPerView: "auto",
        centeredSlides: false,
        centerInsufficientSlides: true,
        centeredSlidesBounds: false,
        spaceBetween: 0,
        breakpoints: {
          767: {
            //   slidesPerView: "auto",
          },
          600: {
            // slidesPerView: 1,
          },
        },
        pagination: false,
        observer: true,
        observeParents: true,
        parallax: true,
      },
      baseFormConfig: {
        fields: {
          fullName: {
            name: "fullName",
            type: "text",
            placeholder: "lp.sections.leads1.form.fullName",
            validation: {
              required: {},
              minLength: {
                min: 5,
              },
            },
          },
          phoneNumber: {
            name: "phoneNumber",
            type: "text",
            placeholder: "lp.sections.leads1.form.phoneNumber",
            validation: {
              required: {},
              cleanIsraelyMobilePhoneNumber: {},
            },
          },
          email: {
            name: "email",
            type: "text",
            placeholder: "lp.sections.leads1.form.email",
            validation: {
              required: {},
              email: {},
            },
          },
          marketingAgreement: {
            name: "marketingAgreement",
            type: "checkbox",
            placeholder: "lp.sections.leads1.form.marketingAgreement",
            validation: {
              checkedCheckBox: {},
            },
          },
          marketingAgreement2: {
            name: "marketingAgreement2",
            type: "checkbox",
            omit: true,
            placeholder: "lp.sections.leads1.form.marketingAgreement2",
            validation: {
              checkedCheckBox: {},
            },
          },
          campaignName: {
            name: "campaignName",
            type: "hidden",
            disabled: true,
            placeholder: "lp.sections.leads1.form.campaignName",
          },
          sourceUrl: {
            name: "sourceUrl",
            type: "hidden",
            disabled: true,
            default: "DYNAMIC_currentUrl",
          },
        },
        groups: {},
        buttons: {
          submit: {
            theme: this.formButtonColor,
            text: "lp.sections.leads1.form.submit",
            iconEnd: "chevrons-inline-end",
            class: "width-expand",
          },
        },
        formHandling: {
          preflight: [
            {
              type: "overloadUrlTrackingParams",
              options: {},
            },
            {
              type: "validation",
              options: {},
            },
          ],
        },
      },
    };
  },
  computed: {
    finalFormConfig() {
      let result = this.baseFormConfig;
      let override = this.formConfig;

      let overrides = _.merge(result, override);

      if (this.isNonEmptyString(this.checkboxLabelTooltipBase)) {
        overrides.fields.marketingAgreement.autoTranslate = false;
        overrides.fields.marketingAgreement.tooltip =
          overrides.fields.marketingAgreement.label;
        overrides.fields.marketingAgreement.label =
          this.checkboxLabelTooltipBase;
        overrides.fields.marketingAgreement.tooltipIconTheme =
          this.formButtonColor;
      }

      if (this.isNonEmptyString(this.checkbox2LabelTooltipBase)) {
        overrides.fields.marketingAgreement2.autoTranslate = false;
        overrides.fields.marketingAgreement2.tooltip =
          overrides.fields.marketingAgreement2.label;
        overrides.fields.marketingAgreement2.label =
          this.checkbox2LabelTooltipBase;
        overrides.fields.marketingAgreement2.tooltipIconTheme =
          this.formButtonColor;
      }

      return overrides;
    },
    titleFeaturesMainTitleColorCssProperty() {
      return `var(--c-${this.featuresMainTitleColor})`;
    },
    formTitleColorCssProperty() {
      return `var(--c-${this.formTitleColor})`;
    },
    finalTitle() {
      let finalTitle = this.title;
      if (
        !Array.isArray(this.dynamicTitleOptions) ||
        this.dynamicTitleOptions.length < 1
      ) {
        return finalTitle;
      }

      this.dynamicTitleOptions.forEach((item) => {
        if (this?.$route?.query?.[item?.paramName] === item?.paramValue) {
          finalTitle = item.titleText;
        }
      });
      return finalTitle;
    },
  },
  methods: {
    submitError() {
      // this.track("leads1|form|submitSuccess", {});
      // this.isSubmitted = true;
    },
    submitHandler() {
      this.track("leads1|form|submitSuccess", {});
      this.isSubmitted = true;
    },
    requestLeadsFormPopupOpenHandler(e) {
      this.isOpened = true;
    },
  },
};
</script>

<style scoped lang="scss">
.leads1 {
  min-height: 100vh;
  min-height: 100lvh;

  .main-image-separator {
    min-width: 2px;

    @media (max-width: 1024px) {
      width: 100%;
      order: 2;
      height: 2px;
    }
  }

  .leads-wrapper {
    min-height: calc(100vh - 80px);
    min-height: calc(100lvh - 80px);
  }

  .legal-wrapper {
    min-height: 80px;
  }

  .content-container {
    flex: 0 0 calc(50% - 1px);
    background: var(--c-gray-1);

    @media (max-width: 1024px) {
      flex: auto;
      order: 3;
    }
  }

  .logo-image {
    max-height: 80px;
    max-width: 320px;

    @media (max-width: 1024px) {
      max-height: 50px;
      max-width: 150px;
    }
  }

  .media-container {
    min-height: calc(100vh - 80px);
    min-height: calc(100lvh - 80px);
    flex: 0 0 calc(50% - 1px);
    background-size: cover;
    background-position: center right;

    @media (max-width: 1024px) {
      min-height: 300px;
      flex: 0 0 300px;
      order: 1;
      background-position: right center;
      background-size: cover;
    }

    :deep(label) {
      color: v-bind(formTitleColorCssProperty);
    }
  }

  .content-container {
    padding: calc(var(--margin-max) * 0.8) calc(var(--margin-2xl) * 1);
    @media (max-width: 1024px) {
      padding: calc(var(--margin-2xl) * 1) calc(var(--margin-m) * 1);
    }

    .main-title {
      font-weight: bold;
      font-size: calc(var(--font-max) * 1.5);
      line-height: 0.85;

      @media (max-width: 1024px) {
        font-size: calc(var(--font-max) * 1);
        line-height: 0.8;
      }
    }

    p,
    :deep(p) {
      font-size: var(--font-2xl);
      line-height: 0.9;
      margin: 0;

      @media (max-width: 1024px) {
        font-size: var(--font-xl);
        line-height: 1.1;
      }
    }
  }

  .features-title {
    @media (max-width: 1024px) {
      text-align: center;
    }
  }

  .features {
    //  margin-top: auto;
  }

  .feature {
    @media (min-width: 1024px) {
      position: relative;
      flex: 25%;

      &::after {
        content: " ";
        position: absolute;
        height: 100%;
        width: 1px;
        top: 0;
        inset-inline-start: calc(100% + 1 * var(--margin-l));
        background-color: v-bind("titleFeaturesMainTitleColorCssProperty");
        opacity: 0.6;
      }

      @media (max-width: 1200px) {
        &::after {
          height: 70%;
          top: 15%;
        }
      }

      &:last-of-type::after {
        display: none;
      }
    }

    @media (max-width: 1026px) {
    }
  }
}

.leads1-mobile-form-modal {
}

.form-container {
  :deep(.tooltip) {
    width: 300px;
    @media (max-width: 1024px) {
    }
  }

  :deep(.field__controls-wrapper--checkbox) {
    [role="tooltip-content"] {
      font-size: var(--font-xs) !important;
      line-height: 1;

      @media (max-width: 1024px) {
        overflow-y: scroll;
        max-height: 130px;
      }
    }
  }
}

.thank-you-container {
  :deep(p) {
    font-size: var(--font-xl);
  }
}
</style>
<style lang="scss">
.leads1-mobile-form-modal {
  &.submitted {
    .modal__inner {
      padding-block: var(--margin-2xl);
    }
  }
}
</style>
